import React, { useEffect, useState } from 'react';
import { Search, X, Check } from 'lucide-react';
import { getTransactions, markTarefaDone, Transaction, Tarefa } from '../services/apiService';

interface Props {
  tarefa: Tarefa;
  onClose: () => void;
  onDone: () => void;
}

function fmtBRL(v: number): string {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export const MarkDoneModal: React.FC<Props> = ({ tarefa, onClose, onDone }) => {
  // Pré-filtra por passivo-conta se houver
  const initialAccount = tarefa.tags['passivo-conta'] || '';
  const [accountFilter, setAccountFilter] = useState(initialAccount);
  const [textFilter, setTextFilter] = useState('');
  const [results, setResults] = useState<Transaction[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const search = async () => {
    if (!accountFilter && !textFilter) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await getTransactions({
        account: accountFilter || undefined,
        description: textFilter || undefined,
      });
      setResults(res.transactions.slice(0, 50));
      setError('');
    } catch (e: any) {
      setError(e?.message || 'Erro ao buscar');
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    // Busca inicial se tem conta
    if (initialAccount) search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSelect = (id: string) => {
    const ns = new Set(selected);
    if (ns.has(id)) ns.delete(id);
    else ns.add(id);
    setSelected(ns);
  };

  const handleConfirm = async () => {
    if (selected.size === 0 && !reason.trim()) {
      setError('Selecione ao menos 1 transação OU forneça um motivo');
      return;
    }
    setSubmitting(true);
    try {
      await markTarefaDone(tarefa['tarefa-id'], {
        resolved_by_txn_ids: Array.from(selected),
        resolved_reason: reason.trim(),
      });
      onDone();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erro ao marcar concluída');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div>
            <h3 className="font-semibold text-gray-900">Marcar como concluída</h3>
            <p className="text-xs text-gray-500 mt-0.5 truncate">{tarefa.title}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-3 border-b border-gray-200">
          <p className="text-sm text-gray-600">
            Selecione 1+ transação(ões) que resolveram esta tarefa (rastreabilidade obrigatória).
          </p>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-xs text-gray-500 block mb-1">Conta (filtra postings)</label>
              <input
                type="text"
                value={accountFilter}
                onChange={(e) => setAccountFilter(e.target.value)}
                placeholder="ex: Passivo:Obrigações:IRRF:2026-01"
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm font-mono"
              />
            </div>
            <div className="flex-1">
              <label className="text-xs text-gray-500 block mb-1">Buscar por descrição</label>
              <input
                type="text"
                value={textFilter}
                onChange={(e) => setTextFilter(e.target.value)}
                placeholder="ex: DARF Tributos"
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                onKeyDown={(e) => e.key === 'Enter' && search()}
              />
            </div>
            <button
              onClick={search}
              disabled={searching}
              className="self-end px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
            >
              <Search className="h-4 w-4" />
              {searching ? '...' : 'Buscar'}
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-1">
          {results.length === 0 && !searching && (
            <div className="text-center text-sm text-gray-500 py-8">
              Sem resultados. Ajuste filtros e clique em Buscar.
            </div>
          )}
          {results.map((t) => {
            const isSelected = selected.has(t.id);
            return (
              <div
                key={t.id}
                onClick={() => toggleSelect(t.id)}
                className={`p-2 border rounded cursor-pointer text-sm flex items-center gap-2 ${
                  isSelected ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input type="checkbox" checked={isSelected} onChange={() => {}} className="pointer-events-none" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-gray-500">{t.date}</span>
                    <span className="font-medium truncate">{t.payee}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 font-mono">
                    {t.postings?.slice(0, 3).map((p, i) => {
                      const amt = p.amount ?? 0;
                      return (
                        <span key={i} className="inline-block mr-3">
                          {p.account.split(':').slice(-2).join(':')} {amt > 0 ? '+' : ''}{fmtBRL(amt)}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="px-4 py-3 border-t border-gray-200 space-y-2">
          {error && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 px-2 py-1 rounded">
              {error}
            </div>
          )}
          <div>
            <label className="text-xs text-gray-500 block mb-1">
              OU justifique sem transação (quando não há txn correspondente)
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="ex: mes-virou, distribuicao-revertida, etc."
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
            />
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs text-gray-500">
              {selected.size > 0 ? `${selected.size} transação(ões) selecionada(s)` : reason ? 'Sem txn — com justificativa' : 'Selecione 1+ ou justifique'}
            </div>
            <div className="flex gap-2">
              <button onClick={onClose} className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50">
                Cancelar
              </button>
              <button
                onClick={handleConfirm}
                disabled={submitting || (selected.size === 0 && !reason.trim())}
                className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
              >
                <Check className="h-4 w-4" />
                Confirmar concluída
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
